// DataSync — Data Broker
// Správa datových zdrojů (NFS, SMB, SFTP, lokální) + sync joby + file browser
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  InlineActions,
  Modal,
  PageHeader,
  RefreshButton,
  SectionHeader,
  Select,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import {
  ArrowRightLeft,
  CheckCircle2,
  ChevronRight,
  Clock,
  Database,
  File,
  Folder,
  FolderOpen,
  Globe,
  HardDrive,
  Network,
  Play,
  PlugZap,
  Plus,
  Server,
  Unplug,
  XCircle,
} from 'lucide-react';

// ── Typy ─────────────────────────────────────────────────────────────────────

type SourceType = 'local' | 'nfs' | 'smb' | 'sftp' | 's3' | 'webdav';
type SyncMode = 'copy' | 'sync' | 'bisync' | 'move';
type SrcStatus = 'unknown' | 'ok' | 'error' | 'mounting';

interface DsSource {
  id: string;
  label: string;
  type: SourceType;
  config: Record<string, unknown>;
  mount_path: string | null;
  enabled: boolean;
  status: SrcStatus;
  status_msg: string | null;
  last_check: string | null;
  created_at: string;
}

interface SyncJob {
  id: string;
  label: string;
  source_id: string;
  dest_id: string;
  source_path: string;
  dest_path: string;
  source_label?: string;
  dest_label?: string;
  schedule: string | null;
  mode: SyncMode;
  watch: boolean;
  delete_dest: boolean;
  filters: Array<{ type: 'include' | 'exclude'; pattern: string }>;
  enabled: boolean;
  last_run: string | null;
  last_status: string | null;
}

interface SyncLog {
  id: number;
  job_id: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  files_transferred: number;
  bytes_transferred: number;
  triggered_by: string;
  output: string | null;
  error: string | null;
}

interface BrowseItem {
  Path: string;
  Name: string;
  Size: number;
  MimeType: string;
  ModTime: string;
  IsDir: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const SOURCE_TYPES: Record<SourceType, { label: string; icon: typeof HardDrive; desc: string }> = {
  local: { label: 'datasync.typeLocal', icon: Folder, desc: 'datasync.typeLocalDesc' },
  nfs: { label: 'datasync.typeNfs', icon: Server, desc: 'datasync.typeNfsDesc' },
  smb: { label: 'datasync.typeSmb', icon: Network, desc: 'datasync.typeSmbDesc' },
  sftp: { label: 'datasync.typeSftp', icon: Globe, desc: 'datasync.typeSftpDesc' },
  s3: { label: 'datasync.typeS3', icon: Database, desc: 'datasync.typeS3Desc' },
  webdav: { label: 'datasync.typeWebdav', icon: Globe, desc: 'datasync.typeWebdavDesc' },
};

const MODE_LABELS: Record<SyncMode, string> = {
  copy: 'datasync.modeCopy',
  sync: 'datasync.modeSync',
  bisync: 'datasync.modeBisync',
  move: 'datasync.modeMove',
};

function statusVariant(s: SrcStatus): 'success' | 'error' | 'gray' | 'yellow' {
  if (s === 'ok') return 'success';
  if (s === 'error') return 'error';
  if (s === 'mounting') return 'yellow';
  return 'gray';
}


function sourceDesc(src: DsSource): string {
  const c = src.config;
  if (src.type === 'local') return String(c['path'] ?? '—');
  if (src.type === 'nfs') return `${c['server']}:${c['export_path']}`;
  if (src.type === 'smb') return `//${c['server']}/${c['share']}`;
  if (src.type === 'sftp') return `${c['username']}@${c['host']}:${c['port'] ?? 22}`;
  if (src.type === 's3') return `${c['bucket'] ?? c['provider']}`;
  if (src.type === 'webdav') return String(c['url'] ?? '');
  return '';
}

// ── Formulář nového zdroje ────────────────────────────────────────────────────

function SourceForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [type, setType] = useState<SourceType>('local');
  const [label, setLabel] = useState('');
  const [cfg, setCfg] = useState<Record<string, string>>({});
  const inp =
    'w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500';

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setCfg((p) => ({ ...p, [k]: e.target.value }));

  const create = useMutation({
    mutationFn: () =>
      api.mcp('datasync.sources', { action: 'create', label, type, config: cfg }),
    onSuccess: () => {
      toast.success(t('datasync.sourceAdded'));
      qc.invalidateQueries({ queryKey: ['ds-sources'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('datasync.newSourceTitle')}
      footer={
        <div className='flex gap-3'>
          <Button variant='secondary' onClick={onClose} className='flex-1'>{t('common.cancel')}</Button>
          <Button
            variant='primary'
            onClick={() => create.mutate()}
            loading={create.isPending}
            disabled={!label || create.isPending}
            className='flex-1'
          >
            {t('datasync.addSource')}
          </Button>
        </div>
      }
    >
      <div className='space-y-4'>
        <div>
          <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.nameRequired')}</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className={inp}
            placeholder={t('datasync.phSourceName')}
          />
        </div>
        <div>
          <label className='text-xs text-gray-400 mb-2 block'>{t('datasync.sourceType')}</label>
          <div className='grid grid-cols-2 gap-2'>
            {(Object.entries(SOURCE_TYPES) as [SourceType, typeof SOURCE_TYPES[SourceType]][]).map((
              [st, info],
            ) => (
              <label
                key={st}
                className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                  type === st
                    ? 'border-blue-500/50 bg-blue-600/10'
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <input
                  type='radio'
                  className='mt-0.5 accent-blue-500'
                  checked={type === st}
                  onChange={() => {
                    setType(st);
                    setCfg({});
                  }}
                />
                <div>
                  <p className='text-xs font-medium text-white'>{t(info.label)}</p>
                  <p className='text-xs text-gray-500 mt-0.5'>{t(info.desc)}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Konfigurace dle typu */}
        {type === 'local' && (
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.serverPathRequired')}</label>
            <input
              value={cfg['path'] ?? ''}
              onChange={set('path')}
              className={inp}
              placeholder={t('datasync.phLocalPath')}
            />
          </div>
        )}
        {type === 'nfs' && (
          <div className='space-y-3'>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.serverRequired')}</label>
                <input
                  value={cfg['server'] ?? ''}
                  onChange={set('server')}
                  className={inp}
                  placeholder='192.168.1.10'
                />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.exportPathRequired')}</label>
                <input
                  value={cfg['export_path'] ?? ''}
                  onChange={set('export_path')}
                  className={inp}
                  placeholder='/export/share'
                />
              </div>
            </div>
            <div>
              <label className='text-xs text-gray-400 mb-1 block'>Mount options</label>
              <input
                value={cfg['options'] ?? ''}
                onChange={set('options')}
                className={inp}
                placeholder='vers=4,rw,soft'
              />
            </div>
            <p className='text-xs text-orange-400/80'>{t('datasync.nfsSudoWarning')}</p>
          </div>
        )}
        {type === 'smb' && (
          <div className='space-y-3'>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.serverRequired')}</label>
                <input
                  value={cfg['server'] ?? ''}
                  onChange={set('server')}
                  className={inp}
                  placeholder={t('datasync.phSmbServer')}
                />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>Share *</label>
                <input
                  value={cfg['share'] ?? ''}
                  onChange={set('share')}
                  className={inp}
                  placeholder='backup'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.userLabel')}</label>
                <input
                  value={cfg['username'] ?? ''}
                  onChange={set('username')}
                  className={inp}
                  placeholder='domain\\user'
                />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.passwordLabel')}</label>
                <input
                  type='password'
                  value={cfg['password'] ?? ''}
                  onChange={set('password')}
                  className={inp}
                />
              </div>
            </div>
            <div>
              <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.domainLabel')}</label>
              <input
                value={cfg['domain'] ?? ''}
                onChange={set('domain')}
                className={inp}
                placeholder='WORKGROUP'
              />
            </div>
          </div>
        )}
        {type === 'sftp' && (
          <div className='space-y-3'>
            <div className='grid grid-cols-3 gap-3'>
              <div className='col-span-2'>
                <label className='text-xs text-gray-400 mb-1 block'>Host *</label>
                <input
                  value={cfg['host'] ?? ''}
                  onChange={set('host')}
                  className={inp}
                  placeholder='backup.server.cz'
                />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>Port</label>
                <input
                  value={cfg['port'] ?? '22'}
                  onChange={set('port')}
                  className={inp}
                  placeholder='22'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.userRequired')}</label>
                <input
                  value={cfg['username'] ?? ''}
                  onChange={set('username')}
                  className={inp}
                  placeholder='backup'
                />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.passwordOrKey')}</label>
                <input
                  type='password'
                  value={cfg['password'] ?? ''}
                  onChange={set('password')}
                  className={inp}
                />
              </div>
            </div>
            <div>
              <label className='text-xs text-gray-400 mb-1 block'>
                {t('datasync.sshKeyPath')}
              </label>
              <input
                value={cfg['key_path'] ?? ''}
                onChange={set('key_path')}
                className={inp}
                placeholder='/root/.ssh/id_rsa'
              />
            </div>
          </div>
        )}
        {type === 's3' && (
          <div className='space-y-3'>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>Provider</label>
                <input
                  value={cfg['provider'] ?? 'AWS'}
                  onChange={set('provider')}
                  className={inp}
                  placeholder='AWS / Minio / Wasabi'
                />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>Bucket</label>
                <input value={cfg['bucket'] ?? ''} onChange={set('bucket')} className={inp} />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>Access Key</label>
                <input
                  value={cfg['access_key'] ?? ''}
                  onChange={set('access_key')}
                  className={inp}
                />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>Secret Key</label>
                <input
                  type='password'
                  value={cfg['secret_key'] ?? ''}
                  onChange={set('secret_key')}
                  className={inp}
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>Region</label>
                <input
                  value={cfg['region'] ?? 'us-east-1'}
                  onChange={set('region')}
                  className={inp}
                />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>Endpoint (MinIO)</label>
                <input
                  value={cfg['endpoint'] ?? ''}
                  onChange={set('endpoint')}
                  className={inp}
                  placeholder='https://minio.example.com'
                />
              </div>
            </div>
          </div>
        )}
        {type === 'webdav' && (
          <div className='space-y-3'>
            <div>
              <label className='text-xs text-gray-400 mb-1 block'>URL *</label>
              <input
                value={cfg['url'] ?? ''}
                onChange={set('url')}
                className={inp}
                placeholder='https://nextcloud.example.com/remote.php/dav/files/user'
              />
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.userLabel')}</label>
                <input value={cfg['username'] ?? ''} onChange={set('username')} className={inp} />
              </div>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.passwordLabel')}</label>
                <input
                  type='password'
                  value={cfg['password'] ?? ''}
                  onChange={set('password')}
                  className={inp}
                />
              </div>
            </div>
            <div>
              <label className='text-xs text-gray-400 mb-1 block'>Vendor</label>
              <input
                value={cfg['vendor'] ?? 'nextcloud'}
                onChange={set('vendor')}
                className={inp}
                placeholder='nextcloud / owncloud / other'
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Záložka: Zdroje ───────────────────────────────────────────────────────────

function SourcesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const { data: sources = [], isLoading, refetch } = useQuery({
    queryKey: ['ds-sources'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; sources: DsSource[] }>('datasync.sources', {
        action: 'list',
      });
      return r.sources ?? [];
    },
    refetchInterval: 30_000,
  });

  const test = useMutation({
    mutationFn: (id: string) =>
      api.mcp<{ ok: boolean; msg?: string }>('datasync.sources', { action: 'test', id }),
    onSuccess: (r, id) => {
      toast.success(`${id}: ${r.msg ?? (r.ok ? 'OK' : 'error')}`);
      qc.invalidateQueries({ queryKey: ['ds-sources'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mount = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'mount' | 'umount' }) =>
      api.mcp<{ ok: boolean; msg?: string }>('datasync.sources', { action, id }),
    onSuccess: (r) => {
      toast.success(r.msg ?? (r.ok ? 'OK' : 'error'));
      qc.invalidateQueries({ queryKey: ['ds-sources'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.mcp('datasync.sources', { action: 'delete', id }),
    onSuccess: () => {
      toast.success(t('datasync.sourceRemoved'));
      qc.invalidateQueries({ queryKey: ['ds-sources'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const list = sources as DsSource[];
  const ok = list.filter((s) => s.status === 'ok').length;

  return (
    <div className='space-y-5'>
      <div className='flex items-center justify-between'>
        <SectionHeader
          title={t('datasync.sourcesTitle')}
          description={t('datasync.sourcesDesc')}
        />
        <div className='flex gap-2'>
          <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
          <Button
            variant='primary'
            size='sm'
            icon={<Plus size={14} />}
            onClick={() => setShowAdd(true)}
          >
            {t('datasync.addSource')}
          </Button>
        </div>
      </div>

      <StatsGrid columns={3}>
        <StatCard
          icon={HardDrive}
          label={t('datasync.totalSources')}
          value={list.length}
          sub={t('datasync.configured')}
        />
        <StatCard
          icon={CheckCircle2}
          label={t('datasync.available')}
          value={ok}
          sub='status OK'
          variant='success'
        />
        <StatCard
          icon={XCircle}
          label={t('datasync.unavailable')}
          value={list.filter((s) => s.status === 'error').length}
          sub='status error'
          variant={list.some((s) => s.status === 'error') ? 'danger' : undefined}
        />
      </StatsGrid>

      {isLoading
        ? <p className='text-sm text-gray-500 py-4'>{t('common.loading')}</p>
        : list.length === 0
        ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<HardDrive className='w-12 h-12 text-gray-700' />}
                title={t('datasync.noSources')}
                description={t('datasync.noSourcesDesc')}
              />
            </CardContent>
          </Card>
        )
        : (
          <div className='space-y-3'>
            {list.map((src) => {
              const TypeIcon = SOURCE_TYPES[src.type]?.icon ?? HardDrive;
              const needsMount = ['nfs', 'smb'].includes(src.type);
              const isMounted = src.status === 'ok';
              return (
                <Card key={src.id}>
                  <CardContent className='py-4'>
                    <div className='flex items-center gap-4'>
                      <div className='p-2.5 rounded-xl bg-gray-800 text-gray-400 shrink-0'>
                        <TypeIcon size={18} />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <p className='text-sm font-semibold text-white'>{src.label}</p>
                          <Badge variant={statusVariant(src.status)} dot>
                            {src.status}
                          </Badge>
                          <Badge variant='gray'>{t(SOURCE_TYPES[src.type]?.label ?? '')}</Badge>
                        </div>
                        <p className='text-xs text-gray-500 font-mono mt-0.5 truncate'>
                          {sourceDesc(src)}
                        </p>
                        {src.status_msg && (
                          <p className='text-xs text-gray-600 mt-0.5 truncate'>{src.status_msg}</p>
                        )}
                      </div>
                      <div className='flex items-center gap-2 shrink-0'>
                        {needsMount && (
                          <Button
                            size='sm'
                            variant={isMounted ? 'warning' : 'secondary'}
                            icon={isMounted ? <Unplug size={12} /> : <PlugZap size={12} />}
                            onClick={() =>
                              mount.mutate({ id: src.id, action: isMounted ? 'umount' : 'mount' })}
                            loading={mount.isPending}
                          >
                            {isMounted ? 'Umount' : 'Mount'}
                          </Button>
                        )}
                        <Button
                          size='sm'
                          variant='ghost'
                          icon={<CheckCircle2 size={12} />}
                          onClick={() => test.mutate(src.id)}
                          loading={test.isPending}
                        >
                          Test
                        </Button>
                        <InlineActions.Row
                          onDelete={() => del.mutate(src.id)}
                          deleteConfirm
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      {showAdd && <SourceForm onClose={() => setShowAdd(false)} />}
    </div>
  );
}

// ── Záložka: Sync joby ────────────────────────────────────────────────────────

function JobsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [logsFor, setLogsFor] = useState<string | null>(null);

  const { data: jobs = [], isLoading, refetch } = useQuery({
    queryKey: ['ds-jobs'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; jobs: SyncJob[] }>('datasync.jobs', {
        action: 'list',
      });
      return r.jobs ?? [];
    },
    refetchInterval: 15_000,
  });

  const { data: sources = [] } = useQuery({
    queryKey: ['ds-sources'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; sources: DsSource[] }>('datasync.sources', {
        action: 'list',
      });
      return r.sources ?? [];
    },
  });

  const { data: logs = [] } = useQuery({
    queryKey: ['ds-logs', logsFor],
    queryFn: async () => {
      if (!logsFor) return [];
      const r = await api.mcp<{ ok: boolean; logs: SyncLog[] }>('datasync.run', {
        action: 'logs',
        job_id: logsFor,
        limit: 10,
      });
      return r.logs ?? [];
    },
    enabled: !!logsFor,
  });

  const run = useMutation({
    mutationFn: (id: string) => api.mcp('datasync.run', { action: 'run', job_id: id }),
    onSuccess: () => {
      toast.success(t('datasync.syncStarted'));
      qc.invalidateQueries({ queryKey: ['ds-jobs'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.mcp('datasync.jobs', { action: 'delete', id }),
    onSuccess: () => {
      toast.success(t('datasync.jobRemoved'));
      qc.invalidateQueries({ queryKey: ['ds-jobs'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const srcList = sources as DsSource[];
  const jobList = jobs as SyncJob[];

  const statusBadge = (s: string | null) => {
    if (!s || s === 'unknown') return <Badge variant='gray'>—</Badge>;
    if (s === 'ok') return <Badge variant='green'>OK</Badge>;
    if (s === 'running') return <Badge variant='blue' dot>{t('datasync.running')}</Badge>;
    return <Badge variant='red'>{t('datasync.errorBadge')}</Badge>;
  };

  return (
    <div className='space-y-5'>
      <div className='flex items-center justify-between'>
        <SectionHeader
          title={t('datasync.jobsTitle')}
          description={t('datasync.jobsDesc')}
        />
        <div className='flex gap-2'>
          <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
          <Button
            variant='primary'
            size='sm'
            icon={<Plus size={14} />}
            onClick={() => setShowAdd(true)}
            disabled={srcList.length < 2}
          >
            {t('datasync.newJob')}
          </Button>
        </div>
      </div>

      {srcList.length < 2 && (
        <div className='p-4 rounded-xl bg-yellow-600/10 border border-yellow-500/20 text-sm text-yellow-300'>
          {t('datasync.needTwoSources')}
        </div>
      )}

      {isLoading
        ? <p className='text-sm text-gray-500 py-4'>{t('common.loading')}</p>
        : jobList.length === 0
        ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<ArrowRightLeft className='w-12 h-12 text-gray-700' />}
                title={t('datasync.noJobs')}
                description={t('datasync.noJobsDesc')}
              />
            </CardContent>
          </Card>
        )
        : (
          <div className='space-y-3'>
            {jobList.map((job) => (
              <Card key={job.id}>
                <CardContent className='py-4'>
                  <div className='flex items-center gap-4'>
                    <div className='p-2.5 rounded-xl bg-gray-800 text-gray-400 shrink-0'>
                      <ArrowRightLeft size={18} />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <p className='text-sm font-semibold text-white'>{job.label}</p>
                        {statusBadge(job.last_status)}
                        <Badge variant='blue'>{t(MODE_LABELS[job.mode])}</Badge>
                        {job.schedule && (
                          <Badge variant='gray'><Clock size={10} /> {job.schedule}</Badge>
                        )}
                        {job.watch && <Badge variant='purple'>Watch</Badge>}
                      </div>
                      <div className='flex items-center gap-1 mt-1 text-xs text-gray-500'>
                        <span className='font-mono text-gray-400'>
                          {job.source_label ?? job.source_id}
                        </span>
                        <ChevronRight size={10} />
                        <span className='font-mono text-gray-400'>
                          {job.dest_label ?? job.dest_id}
                        </span>
                        {job.source_path !== '/' && (
                          <span className='text-gray-600'>({job.source_path})</span>
                        )}
                      </div>
                      {job.last_run && (
                        <p className='text-xs text-gray-600 mt-0.5'>
                          {t('datasync.lastRun', { date: fmtDate(job.last_run) })}
                        </p>
                      )}
                    </div>
                    <div className='flex items-center gap-2 shrink-0'>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() => setLogsFor(logsFor === job.id ? null : job.id)}
                      >
                        {t('datasync.logs')}
                      </Button>
                      <Button
                        size='sm'
                        variant='secondary'
                        icon={<Play size={11} />}
                        onClick={() => run.mutate(job.id)}
                        loading={run.isPending}
                      >
                        {t('datasync.run')}
                      </Button>
                      <InlineActions.Row onDelete={() => del.mutate(job.id)} deleteConfirm />
                    </div>
                  </div>

                  {/* Inline logy */}
                  {logsFor === job.id && (
                    <div className='mt-4 pt-4 border-t border-gray-800'>
                      {(logs as SyncLog[]).length === 0
                        ? <p className='text-xs text-gray-600'>{t('datasync.noRecords')}</p>
                        : (
                          <div className='space-y-2'>
                            {(logs as SyncLog[]).map((l) => (
                              <div key={l.id} className='flex items-start gap-3 text-xs'>
                                <span
                                  className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${
                                    l.status === 'ok'
                                      ? 'bg-green-500'
                                      : l.status === 'running'
                                      ? 'bg-blue-500 animate-pulse'
                                      : 'bg-red-500'
                                  }`}
                                />
                                <div className='flex-1 min-w-0'>
                                  <div className='flex items-center gap-3'>
                                    <span className='text-gray-400'>{fmtDate(l.started_at)}</span>
                                    <span className='text-gray-500'>
                                      {t('datasync.filesCount', { count: l.files_transferred })}
                                    </span>
                                    <span className='text-gray-600'>{l.triggered_by}</span>
                                  </div>
                                  {l.error && (
                                    <p className='text-red-400 mt-0.5 truncate'>{l.error}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {showAdd && srcList.length >= 2 && (
        <NewJobModal
          sources={srcList}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}

function NewJobModal({ sources, onClose }: { sources: DsSource[]; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [srcId, setSrcId] = useState(sources[0]?.id ?? '');
  const [dstId, setDstId] = useState(sources[1]?.id ?? '');
  const [srcPath, setSrcPath] = useState('/');
  const [dstPath, setDstPath] = useState('/');
  const [mode, setMode] = useState<SyncMode>('copy');
  const [schedule, setSchedule] = useState('');
  const sel =
    'w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500';
  const inp = sel;

  const create = useMutation({
    mutationFn: () =>
      api.mcp('datasync.jobs', {
        action: 'create',
        label,
        source_id: srcId,
        dest_id: dstId,
        source_path: srcPath,
        dest_path: dstPath,
        mode,
        schedule: schedule || undefined,
      }),
    onSuccess: () => {
      toast.success(t('datasync.jobCreated'));
      qc.invalidateQueries({ queryKey: ['ds-jobs'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('datasync.newJobTitle')}
      footer={
        <div className='flex gap-3'>
          <Button variant='secondary' onClick={onClose} className='flex-1'>{t('common.cancel')}</Button>
          <Button
            variant='primary'
            onClick={() => create.mutate()}
            loading={create.isPending}
            disabled={!label || !srcId || !dstId}
            className='flex-1'
          >
            {t('datasync.create')}
          </Button>
        </div>
      }
    >
      <div className='space-y-4'>
        <div>
          <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.nameRequired')}</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className={inp}
            placeholder={t('datasync.phJobName')}
          />
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.sourceRequired')}</label>
            <Select value={srcId} onChange={(e) => setSrcId(e.target.value)}>
              {sources.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </Select>
          </div>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.destRequired')}</label>
            <Select value={dstId} onChange={(e) => setDstId(e.target.value)}>
              {sources.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </Select>
          </div>
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.sourcePath')}</label>
            <input
              value={srcPath}
              onChange={(e) => setSrcPath(e.target.value)}
              className={inp}
              placeholder='/'
            />
          </div>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.destPath')}</label>
            <input
              value={dstPath}
              onChange={(e) => setDstPath(e.target.value)}
              className={inp}
              placeholder='/'
            />
          </div>
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.mode')}</label>
            <Select
              value={mode}
              onChange={(e) => setMode(e.target.value as SyncMode)}
            >
              {(Object.entries(MODE_LABELS) as [SyncMode, string][]).map(([v, l]) => (
                <option key={v} value={v}>{t(l)}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('datasync.cronOptional')}</label>
            <input
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              className={inp}
              placeholder='0 2 * * *'
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Záložka: Prohlížeč ────────────────────────────────────────────────────────

function BrowserTab() {
  const { t } = useTranslation();
  const { data: sources = [] } = useQuery({
    queryKey: ['ds-sources'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; sources: DsSource[] }>('datasync.sources', {
        action: 'list',
      });
      return r.sources ?? [];
    },
  });

  const [selectedSrc, setSelectedSrc] = useState<string>('');
  const [path, setPath] = useState('/');
  const [browsePath, setBrowsePath] = useState<{ id: string; path: string } | null>(null);

  const { data: browse, isLoading: browseLoading } = useQuery({
    queryKey: ['ds-browse', browsePath],
    queryFn: async () => {
      if (!browsePath) return null;
      // MCP browse uses camelCase fields; map to UI BrowseItem shape
      const r = await api.mcp<{
        ok: boolean;
        items?: Array<{ name: string; isDir: boolean; size: number; modified?: string }>;
        error?: string;
      }>('datasync.browse', { source_id: browsePath.id, path: browsePath.path });
      if (!r.ok) throw new Error(r.error ?? 'Browse failed');
      const items: BrowseItem[] = (r.items ?? []).map((it) => ({
        Path: it.name,
        Name: it.name,
        Size: it.size,
        MimeType: it.isDir ? 'inode/directory' : 'application/octet-stream',
        ModTime: it.modified ?? '',
        IsDir: it.isDir,
      }));
      return { items };
    },
    enabled: !!browsePath,
  });

  const srcList = sources as DsSource[];
  const items = (browse as { items: BrowseItem[] } | null)?.items ?? [];

  const navigate = (item: BrowseItem) => {
    if (!item.IsDir || !browsePath) return;
    const newPath = browsePath.path.replace(/\/$/, '') + '/' + item.Name;
    setBrowsePath({ id: browsePath.id, path: newPath });
    setPath(newPath);
  };

  const goUp = () => {
    if (!browsePath || browsePath.path === '/') return;
    const parts = browsePath.path.replace(/\/$/, '').split('/');
    parts.pop();
    const newPath = parts.join('/') || '/';
    setBrowsePath({ id: browsePath.id, path: newPath });
    setPath(newPath);
  };

  return (
    <div className='space-y-4'>
      <SectionHeader
        title={t('datasync.browserTitle')}
        description={t('datasync.browserDesc')}
      />

      <div className='flex gap-3'>
        <Select
          value={selectedSrc}
          onChange={(e) => setSelectedSrc(e.target.value)}
          className='flex-1'
        >
          <option value=''>{t('datasync.selectSource')}</option>
          {srcList.map((s) => (
            <option key={s.id} value={s.id}>{s.label} ({t(SOURCE_TYPES[s.type]?.label ?? '')})</option>
          ))}
        </Select>
        <Button
          variant='primary'
          disabled={!selectedSrc}
          onClick={() => {
            setBrowsePath({ id: selectedSrc, path: '/' });
            setPath('/');
          }}
        >
          {t('datasync.browse')}
        </Button>
      </div>

      {browsePath && (
        <Card>
          <CardHeader>
            <div className='flex items-center gap-3'>
              <Button size='sm' variant='ghost' onClick={goUp} disabled={path === '/'}>
                {t('datasync.up')}
              </Button>
              <code className='text-xs text-blue-400 font-mono flex-1 truncate'>
                {srcList.find((s) => s.id === browsePath.id)?.label} : {path}
              </code>
            </div>
          </CardHeader>
          <CardContent className='p-0'>
            {browseLoading
              ? <p className='text-sm text-gray-500 p-6'>{t('common.loading')}</p>
              : items.length === 0
              ? <p className='text-sm text-gray-600 p-6 text-center'>{t('datasync.emptyFolder')}</p>
              : (
                <div className='divide-y divide-gray-800/50'>
                  {items.map((item) => (
                    <div
                      key={item.Path}
                      className={`flex items-center gap-3 px-4 py-2.5 ${
                        item.IsDir ? 'cursor-pointer hover:bg-gray-800/40' : ''
                      }`}
                      onClick={() => item.IsDir && navigate(item)}
                    >
                      {item.IsDir
                        ? <FolderOpen size={15} className='text-yellow-400 shrink-0' />
                        : <File size={15} className='text-gray-500 shrink-0' />}
                      <span className='flex-1 text-sm text-white font-mono truncate'>
                        {item.Name}
                      </span>
                      {!item.IsDir && (
                        <span className='text-xs text-gray-500 shrink-0'>
                          {fmtBytes(item.Size)}
                        </span>
                      )}
                      <span className='text-xs text-gray-600 shrink-0 hidden md:block'>
                        {fmtDate(item.ModTime)}
                      </span>
                      {item.IsDir && <ChevronRight size={13} className='text-gray-600 shrink-0' />}
                    </div>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Stránka ───────────────────────────────────────────────────────────────────

export default function DataSync() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title='DataSync'
        description={t('datasync.pageDescription')}
        icon={HardDrive}
      />
      <Tabs defaultTab='sources'>
        <TabList>
          <Tab id='sources' icon={<HardDrive size={14} />}>{t('datasync.sourcesTab')}</Tab>
          <Tab id='jobs' icon={<ArrowRightLeft size={14} />}>{t('datasync.syncTab')}</Tab>
          <Tab id='browser' icon={<FolderOpen size={14} />}>{t('datasync.browserTab')}</Tab>
        </TabList>
        <div className='mt-6'>
          <TabPanel id='sources'>
            <SourcesTab />
          </TabPanel>
          <TabPanel id='jobs'>
            <JobsTab />
          </TabPanel>
          <TabPanel id='browser'>
            <BrowserTab />
          </TabPanel>
        </div>
      </Tabs>
    </div>
  );
}
